'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import ChevronLeftIcon from '@/components/icons/ChevronLeftIcon';
import ChevronRightIcon from '@/components/icons/ChevronRightIcon';
import { Button } from '@/components';
import MediaPlaceholder from '@/components/ui/MediaPlaceholder';

type Props = {
  storeName: string;
  /** Which store's gallery to load from /api/location/explore. */
  covaLocationId?: number | null;
};

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:4000';

/**
 * The five slots were hardcoded to the product placeholder, so every store
 * showed the same grey silhouette five times. Images are configured per store
 * in the admin panel and fall back to that store's own Google photos.
 */
function useExploreImages(covaLocationId?: number | null): string[] {
  const [images, setImages] = useState<string[]>([]);

  useEffect(() => {
    if (!covaLocationId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/location/explore?covaLocationId=${covaLocationId}`, {
          cache: 'no-store',
        });
        const data = await res.json();
        if (!cancelled && data?.success && Array.isArray(data.images)) setImages(data.images);
      } catch {
        // Placeholder art stays; the section is decorative.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [covaLocationId]);

  return images;
}

/** Tiles per slide, the 2x2 bento layout. */
const PER_SLIDE = 4;

/**
 * The photo for one tile, or '' when the store has run out.
 *
 * This used to wrap with a modulo, and the grid ignored which slide it was on,
 * so all four pages showed the same four pictures. Stores carry nine or ten
 * photos each now, so paging through them shows different ones and stops
 * rather than looping.
 */
function slotImage(images: string[], index: number): string {
  return images[index] ?? '';
}

/** Enough slides to show every photo once, and no emptier slide beyond that. */
function slideCount(images: string[]): number {
  return Math.max(1, Math.ceil(images.length / PER_SLIDE));
}

/** Design ratios: wide tiles 787×343, narrow tiles 557×343 */
const ASPECT_WIDE = 'aspect-[787/343]';
const ASPECT_NARROW = 'aspect-[557/343]';

const MOBILE_EXPLORE_ITEMS = [
  { key: 'wide-1', aspect: ASPECT_WIDE, widthClassName: 'w-[242px]', heightClassName: 'h-[242px]' },
  { key: 'narrow-1', aspect: ASPECT_NARROW, widthClassName: 'w-[242px]', heightClassName: 'h-[242px]' },
  { key: 'narrow-2', aspect: ASPECT_NARROW, widthClassName: 'w-[242px]', heightClassName: 'h-[242px]' },
  { key: 'wide-2', aspect: ASPECT_WIDE, widthClassName: 'w-[242px]', heightClassName: 'h-[242px]' },
] as const;

function ExploreMobileCard({
  aspect,
  widthClassName,
  heightClassName,
  src,
}: {
  aspect: string;
  widthClassName: string;
  heightClassName: string;
  src: string;
}) {
  return (
    <div className={`relative ${widthClassName} ${heightClassName} shrink-0 overflow-hidden rounded-2xl bg-neutral-10 ${aspect}`}>
      {src ? (
        <Image src={src} alt="" fill className="object-cover" sizes="(max-width: 640px) 80vw, 282px" unoptimized />
      ) : (
        <MediaPlaceholder />
      )}
    </div>
  );
}

/**
 * One tile in the bento grid.
 *
 * A store can hold nine or ten photos, which does not divide evenly by four, so
 * the last slide has holes in it. A hole gets the stand-in rather than a repeat
 * of an earlier photo, which is what the old modulo did.
 */
function ExploreTile({
  src,
  aspect,
  colSpan,
  sizes,
  priority,
}: {
  src: string;
  aspect: string;
  colSpan: string;
  sizes: string;
  priority?: boolean;
}) {
  return (
    <div className={`relative w-full overflow-hidden rounded-2xl bg-neutral-10 ${aspect} ${colSpan}`}>
      {src ? (
        <Image src={src} alt="" fill className="object-cover" sizes={sizes} priority={priority} />
      ) : (
        <MediaPlaceholder />
      )}
    </div>
  );
}

function ExploreSlideGrid({ slideIndex, images }: { slideIndex: number; images: string[] }) {
  return (
    <div className="grid w-full grid-cols-1 gap-4 lg:grid-cols-12">
      <ExploreTile
        src={slotImage(images, slideIndex * PER_SLIDE + 0)}
        aspect={ASPECT_WIDE}
        colSpan="lg:col-span-7"
        sizes="(max-width: 1024px) 100vw, 58vw"
        priority={slideIndex === 0}
      />
      <ExploreTile
        src={slotImage(images, slideIndex * PER_SLIDE + 1)}
        aspect={ASPECT_NARROW}
        colSpan="lg:col-span-5"
        sizes="(max-width: 1024px) 100vw, 42vw"
      />
      <ExploreTile
        src={slotImage(images, slideIndex * PER_SLIDE + 2)}
        aspect={ASPECT_NARROW}
        colSpan="lg:col-span-5"
        sizes="(max-width: 1024px) 100vw, 42vw"
      />
      <ExploreTile
        src={slotImage(images, slideIndex * PER_SLIDE + 3)}
        aspect={ASPECT_WIDE}
        colSpan="lg:col-span-7"
        sizes="(max-width: 1024px) 100vw, 58vw"
      />
    </div>
  );
}

export default function LocationExploreSection({ storeName, covaLocationId }: Props) {
  const images = useExploreImages(covaLocationId);
  const [slideIndex, setSlideIndex] = useState(0);
  const slides = slideCount(images);
  const mobileScrollerRef = useRef<HTMLDivElement | null>(null);
  const mobileItemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const atStart = slideIndex <= 0;
  const atEnd = slideIndex >= slides - 1;

  const scrollToMobileItem = useCallback((index: number) => {
    const container = mobileScrollerRef.current;
    const item = mobileItemRefs.current[index];
    if (!container || !item) return;

    container.scrollTo({
      left: item.offsetLeft - container.offsetLeft,
      behavior: 'smooth',
    });
  }, []);

  const updateMobileIndexFromScroll = useCallback(() => {
    const container = mobileScrollerRef.current;
    if (!container) return;

    const closestIndex = mobileItemRefs.current.reduce((bestIndex, item, index) => {
      if (!item) return bestIndex;

      const currentDistance = Math.abs(item.offsetLeft - container.scrollLeft);
      const bestItem = mobileItemRefs.current[bestIndex];
      const bestDistance = bestItem
        ? Math.abs(bestItem.offsetLeft - container.scrollLeft)
        : Number.POSITIVE_INFINITY;

      return currentDistance < bestDistance ? index : bestIndex;
    }, 0);

    setSlideIndex(Math.floor(closestIndex / PER_SLIDE));
  }, []);

  const handleStep = useCallback(
    (direction: 'prev' | 'next') => {
      const isMobile = window.innerWidth < 640;

      setSlideIndex((current) => {
        const nextIndex =
          direction === 'prev'
            ? Math.max(0, current - 1)
            : Math.min(slides - 1, current + 1);

        if (isMobile) {
          scrollToMobileItem(nextIndex * PER_SLIDE);
        }

        return nextIndex;
      });
    },
    [scrollToMobileItem, slides]
  );

  return (
    <section className="relative mt-4 sm:mt-6" aria-labelledby="location-explore-heading">
      <div className="mx-auto w-full px-4 sm:px-8">
        <div className="pb-8 pt-2 sm:pb-12 sm:pt-4">
          <div className="mb-5 grid grid-cols-1 gap-3 lg:grid-cols-12 lg:items-start">
            <h2
              id="location-explore-heading"
              className="text-h3-mobile font-bold leading-tight text-blue-100 sm:text-h3-desktop lg:col-span-7"
            >
              {`Explore Dank ${storeName}`}
            </h2>
            <p className="text-body-14-desktop font-medium text-blue-100/80 sm:text-body-16-desktop lg:col-span-5">
              Designed for a relaxed, stress-free experience. Come say hi to our team and browse our full collection in
              person
            </p>
          </div>

          <div
            ref={mobileScrollerRef}
            className="sm:hidden overflow-x-auto scrollbar-hide"
            onScroll={updateMobileIndexFromScroll}
          >
            <div className="flex w-max gap-3">
              {images.map((src, index) => {
                const item = MOBILE_EXPLORE_ITEMS[index % MOBILE_EXPLORE_ITEMS.length];
                return (
                  <div
                    key={`${item.key}-${index}`}
                    ref={(element) => {
                      mobileItemRefs.current[index] = element;
                    }}
                  >
                    <ExploreMobileCard
                      aspect={item.aspect}
                      widthClassName={item.widthClassName}
                      heightClassName={item.heightClassName}
                      src={src}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          <div className="hidden sm:block overflow-hidden">
            <div
              className="flex transition-transform duration-300 ease-out"
              style={{
                width: `${slides * 100}%`,
                transform: `translateX(-${(100 / slides) * slideIndex}%)`,
              }}
            >
              {Array.from({ length: slides }, (_, i) => (
                <div key={i} style={{ width: `${100 / slides}%` }} className="shrink-0">
                  <ExploreSlideGrid slideIndex={i} images={images} />
                </div>
              ))}
            </div>
          </div>

          <div className="mt-5 items-center gap-3 flex">
            <div className="h-px flex-1 bg-neutral-15" />
            <Button
              variant="bordered"
              size="icon-sm"
              aria-label="Previous images"
              disabled={atStart}
              onClick={() => handleStep('prev')}
              className="!rounded-full"
            >
              <ChevronLeftIcon size={14} color="currentColor" />
            </Button>
            <Button
              variant="bordered"
              size="icon-sm"
              aria-label="Next images"
              disabled={atEnd}
              onClick={() => handleStep('next')}
              className="!rounded-full"
            >
              <ChevronRightIcon size={14} color="currentColor" />
            </Button>
            <div className="h-px flex-1 bg-neutral-15" />
          </div>
        </div>
      </div>
    </section>
  );
}
